import React, { useCallback, useEffect, useRef } from 'react'
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom'
import { ToastProvider } from './components/feedback/Toast'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import AppLayoutShell from './components/layouts/AppLayoutShell.jsx'
import AppRoutes from './routes/AppRoutes.jsx'
import { useStore } from './store/index.js'
import { useTenantModules } from './hooks/useTenantModules.js'
import { useUserPreferences } from './hooks/useUserPreferences.js'
import { normalizeLogoPath } from './utils/branding.js'
import fedisLogo from './assets/fedis.png'
import { apiFetch, clearToken, fetchMe, getToken, logout as apiLogout, setToken } from './api.js'
import { getOnboardingStatus } from './api/settings.api.js'
import { landingPathForRoles } from './routes/routeUtils.js'

const INITIAL_PRODUCTS = [
  {
    id: 'F-001',
    name: 'Filtre à eau - Grand modèle',
    stock_quantity: 50,
    unit_price: 45000,
    stock_min: 5,
  },
  {
    id: 'F-002',
    name: 'Filtre à eau - Petit modèle',
    stock_quantity: 120,
    unit_price: 28000,
    stock_min: 10,
  },
  {
    id: 'B-001',
    name: 'Bougie à eau (cartouche)',
    stock_quantity: 300,
    unit_price: 5000,
    stock_min: 30,
  },
]

const INACTIVITY_LIMIT = 5 * 60 * 1000

export default function App() {
  const inRouter = useInRouterContext()
  const location = inRouter ? useLocation() : { pathname: '(no-router)' }
  const navigate = inRouter ? useNavigate() : null

  if (!inRouter) {
    console.error('[FEDIS] App.jsx est rendu hors <BrowserRouter>. Verifie src/main.jsx.')
    return (
      <div className="p-6">
        <div className="mb-3 p-3 rounded-lg border bg-red-50 text-red-800">
          <strong>Erreur de routage :</strong> l'application est rendue hors contexte Router.
        </div>
        <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded">
          {`// src/main.jsx (exemple)
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App.jsx'

ReactDOM.createRoot(document.getElementById('root')).render(
  <BrowserRouter>
    <App />
  </BrowserRouter>
)`}
        </pre>
      </div>
    )
  }

  const user = useStore((s) => s.user)
  const setAuth = useStore((s) => s.setAuth)
  const storeLogout = useStore((s) => s.logout)
  const products = useStore((s) => s.products)
  const setProducts = useStore((s) => s.setProducts)
  const addOrder = useStore((s) => s.addOrder)
  const locked = useStore((s) => s.locked)
  const setLocked = useStore((s) => s.setLocked)

  const inactivityTimerRef = useRef(null)
  const connected = !!user
  const { modules, isModuleEnabled } = useTenantModules()
  const { preferences, updatePreferences } = useUserPreferences()
  const [onboardingChecked, setOnboardingChecked] = React.useState(false)
  const [onboardingCompleted, setOnboardingCompleted] = React.useState(true)

  const hasRole = useCallback(
    (roleName) => !!(user && Array.isArray(user.roles) && user.roles.includes(roleName)),
    [user]
  )

  const applyBrowserBranding = useCallback((name, logoPath) => {
    const cleanName = String(name || '').trim()
    document.title = cleanName || 'Fedis'

    const iconHref = normalizeLogoPath(logoPath) || fedisLogo
    let icon = document.querySelector('link[rel="icon"]')
    if (!icon) {
      icon = document.createElement('link')
      icon.setAttribute('rel', 'icon')
      document.head.appendChild(icon)
    }
    icon.setAttribute('href', iconHref)
  }, [])

  useEffect(() => {
    let active = true

    const loadBranding = async () => {
      if (!connected) {
        applyBrowserBranding('Fedis', fedisLogo)
        return
      }
      try {
        const invoice = await apiFetch('/api/settings/invoice-format')
        if (!active) return
        applyBrowserBranding(
          invoice?.company_name || 'Fedis',
          normalizeLogoPath(invoice?.logo_path)
        )
      } catch {
        if (!active) return
        applyBrowserBranding('Fedis', fedisLogo)
      }
    }

    loadBranding()
    window.addEventListener('fedis-branding-updated', loadBranding)
    return () => {
      active = false
      window.removeEventListener('fedis-branding-updated', loadBranding)
    }
  }, [connected, applyBrowserBranding])

  const reloadProducts = useCallback(async () => {
    if (!connected) {
      setProducts([])
      return
    }
    try {
      const data = await apiFetch('/api/products')
      const normalized = Array.isArray(data)
        ? data.map((p) => ({
            ...p,
            price: Number(p.unit_price ?? p.price ?? 0),
            stock_quantity: Number(p.stock_quantity ?? p.stock ?? 0),
          }))
        : []
      setProducts(normalized)
    } catch (e) {
      console.warn('Produits API indisponible, fallback memoire:', e?.message || e)
      setProducts(INITIAL_PRODUCTS)
    }
  }, [connected, setProducts])

  const onCreateSale = useCallback(
    (invoiceLikeObject) => {
      addOrder(invoiceLikeObject)
    },
    [addOrder]
  )

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }
  }, [])

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer()
    if (!connected || locked) return
    inactivityTimerRef.current = setTimeout(() => {
      if (connected) setLocked(true)
    }, INACTIVITY_LIMIT)
  }, [clearInactivityTimer, connected, locked, setLocked])

  const onLoggedIn = useCallback(
    (loginResponse) => {
      if (loginResponse?.token) setToken(loginResponse.token)
      else clearToken()
      if (loginResponse?.user) setAuth(loginResponse.user, loginResponse.token || null)

      const roles = loginResponse?.user?.roles || []
      if (navigate) navigate(landingPathForRoles(roles), { replace: true })

      setTimeout(() => reloadProducts(), 100)
      setLocked(false)
      resetInactivityTimer()
    },
    [navigate, reloadProducts, resetInactivityTimer, setAuth, setLocked]
  )

  const onLogout = useCallback(async () => {
    try {
      await apiLogout()
    } catch (err) {
      console.warn('Logout backend impossible :', err?.message || err)
    }
    clearToken()
    storeLogout()
    setProducts([])
    setLocked(false)
    clearInactivityTimer()
    if (navigate) navigate('/auth', { replace: true })
  }, [clearInactivityTimer, navigate, setLocked, setProducts, storeLogout])

  const handleUnlock = useCallback(() => {
    if (!connected) {
      setLocked(false)
      return
    }
    setLocked(false)
    resetInactivityTimer()
  }, [connected, resetInactivityTimer, setLocked])

  useEffect(() => {
    function poke() {
      if (!locked) resetInactivityTimer()
    }
    window.addEventListener('mousemove', poke)
    window.addEventListener('keydown', poke)
    window.addEventListener('click', poke)
    window.addEventListener('touchstart', poke)
    return () => {
      window.removeEventListener('mousemove', poke)
      window.removeEventListener('keydown', poke)
      window.removeEventListener('click', poke)
      window.removeEventListener('touchstart', poke)
    }
  }, [locked, resetInactivityTimer])

  useEffect(() => {
    if (connected) resetInactivityTimer()
    else {
      clearInactivityTimer()
      if (locked) setLocked(false)
    }
  }, [connected, locked, resetInactivityTimer, clearInactivityTimer, setLocked])

  useEffect(() => {
    if (locked) clearInactivityTimer()
    else resetInactivityTimer()
  }, [locked, clearInactivityTimer, resetInactivityTimer])

  useEffect(() => {
    ;(async () => {
      try {
        const me = await fetchMe()
        setAuth(me, getToken() || null)
        setLocked(false)
        resetInactivityTimer()
        if (navigate) navigate(landingPathForRoles(me.roles || []), { replace: true })
      } catch (err) {
        console.warn('Impossible de charger /auth/me :', err?.message || err)
        clearToken()
        storeLogout()
        clearInactivityTimer()
        setLocked(false)
        if (navigate) navigate('/auth', { replace: true })
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (connected) reloadProducts()
  }, [connected, reloadProducts])

  useEffect(() => {
    let alive = true
    if (!connected) {
      setOnboardingChecked(false)
      setOnboardingCompleted(true)
      return () => {
        alive = false
      }
    }
    ;(async () => {
      try {
        const status = await getOnboardingStatus()
        if (!alive) return
        setOnboardingCompleted(!!status?.completed)
      } catch {
        if (!alive) return
        setOnboardingCompleted(true)
      } finally {
        if (alive) setOnboardingChecked(true)
      }
    })()
    return () => {
      alive = false
    }
  }, [connected, user?.id])

  useEffect(() => {
    if (!navigate) return
    if (!connected || !onboardingChecked || onboardingCompleted) return
    const p = location.pathname
    const bypass =
      p === '/onboarding' ||
      p === '/auth' ||
      p === '/signup' ||
      p === '/verify-email' ||
      p === '/forgot-password' ||
      p.startsWith('/reset-password') ||
      p === '/installments'
    if (!bypass) navigate('/onboarding', { replace: true })
  }, [connected, onboardingChecked, onboardingCompleted, location.pathname, navigate])

  useEffect(() => {
    console.debug('[FEDIS] Router OK =', inRouter, '| path =', location.pathname)
  }, [inRouter, location.pathname])

  return (
    <ErrorBoundary>
      <ToastProvider />
      <AppRoutes
        connected={connected}
        user={user}
        onLoggedIn={onLoggedIn}
        products={products}
        reloadProducts={reloadProducts}
        onCreateSale={onCreateSale}
        hasRole={hasRole}
        isModuleEnabled={isModuleEnabled}
        layoutElement={
          <AppLayoutShell
            user={user}
            onLogout={onLogout}
            hasRole={hasRole}
            locked={locked}
            onUnlock={handleUnlock}
            modules={modules}
            isModuleEnabled={isModuleEnabled}
            preferences={preferences}
            updatePreferences={updatePreferences}
          />
        }
      />
    </ErrorBoundary>
  )
}
